import type {
  contentType,
  ISitemapNuxtRouteParams,
  ISitemapNuxtSeoParams,
  ISitemapRemap,
  Sitemap,
  TNavTypes,
} from '~/interfaces/dto/sitemap/response';
import { getAbsoluteUrl } from '~/utilities/useAbsoluteUrl';
import type { UseSeoMetaInput } from '@unhead/vue/types';

export const remapSitemap = (
  sitemap: Sitemap[],
  parentType?: contentType,
): Partial<ISitemapRemap>[] => {
  const nav: TNavTypes[] = ['top', 'left', 'footer', 'blog', 'secondary'];
  const sm = [];

  for (const {
    P: Path,
    C: Caption,
    N: Navigation,
    PN: PageName,
    A: Alias,
    RURL: RelativeUrl,
    PD: DocumentPageDescription,
    PKW: DocumentPageKeyWords,
    PT: DocumentPageTitle,
    CH: Children,
    M: Mapping,
    MA: MobileApp,
  } of sitemap) {
    const isHome = MobileApp?.component === 'HomeComponent';
    const isContent =
      MobileApp?.component === 'WebViewComponent' ||
      MobileApp?.component === 'ContactUsComponent' ||
      MobileApp?.component === 'PromotionComponent' ||
      MobileApp?.component === 'PromopagesComponent' ||
      MobileApp?.component === '';

    const isVertical =
      MobileApp?.component === 'CasinoComponent' ||
      MobileApp?.component === 'HomeComponent';

    const isBlog = Path.toLowerCase().startsWith('/blog');
    const isSecondary = Path.toLowerCase().startsWith('/secondary-navigation');

    const vertical = isVertical && !!Mapping ? Mapping : 'Home';

    const contentType: contentType = (() => {
      if (isVertical) return 'vertical';
      if (isContent) return 'content';
      if (parentType === 'blog') return 'blog-category';
      if (isBlog) return 'blog';
      if (isSecondary) return 'secondary';
      return 'general';
    })();

    const absolutePath = (() => {
      if (!!RelativeUrl) return RelativeUrl;
      return Path?.toLowerCase();
    })();

    const nuxtRouteParams: ISitemapNuxtRouteParams = {
      name: '',
      params: {},
      path: absolutePath,
    };

    const pathParts = absolutePath
      .split('/')
      .filter((i) => !!i)
      .map((i) => i?.toLowerCase());

    switch (contentType) {
      case 'vertical':
      case 'content':
        Object.assign(nuxtRouteParams, {
          name: 'contentType',
          params: {
            contentType: pathParts[0],
          },
        });
        break;
      case 'blog':
        Object.assign(nuxtRouteParams, {
          name: 'blog',
        });
        break;
      case 'blog-category':
        Object.assign(nuxtRouteParams, {
          name: 'blog-category',
          params: {
            category: pathParts[1],
          },
        });
        break;
    }

    const nuxtSeoParams: Partial<ISitemapNuxtSeoParams> = {
      applicationName: 'JPC',
      title: DocumentPageTitle || `${PageName} - Jackpot city`,
      description: DocumentPageDescription || 'Jackpot city',
      ogTitle: DocumentPageTitle || `${PageName} - Jackpot city`,
      twitterTitle: DocumentPageTitle || `${PageName} - Jackpot city`,
      twitterDescription: DocumentPageDescription || 'Jackpot city',
      ogDescription: DocumentPageDescription || 'Jackpot city',
      ogImage:
        'https://media.jpc.africa/medialibraries/content.jpc.africa/branding/jpccasino_sa_placeholder_500x500.jpg',
      ogImageAlt: 'Jackpot City',
      twitterImage:
        'https://media.jpc.africa/medialibraries/content.jpc.africa/branding/jpccasino_sa_placeholder_500x500.jpg',
      ogUrl: getAbsoluteUrl(Path),
      twitterCard: 'summary',
      keywords: DocumentPageKeyWords,
    };
    sm.push({
      nuxtRouteParams,
      nuxtSeoParams,
      Path: absolutePath,
      Caption,
      Navigation,
      PageName,
      Alias,
      Mapping,
      MobileApp,
      RelativeUrl,
      Children: !!Children.length ? remapSitemap(Children, contentType) : [],
      isHome,
      isContent,
      isVertical,
      isSecondary,
      isBlog,
      vertical,
      contentType,
    });
  }

  if (!parentType) {
    const menus: Record<TNavTypes, Partial<ISitemapRemap>[]> = {
      top: [],
      footer: [],
      left: [],
      blog: [],
      secondary: [],
    };

    nav.forEach((n) => {
      if (n === 'blog')
        return (menus[n] = sm.find((s) => s.isBlog)?.Children || []);
      if (n === 'secondary')
        return (menus[n] = sm.find((s) => s.isSecondary)?.Children || []);
      menus[n] = sm.filter((s) => s.Navigation[n]);
    });
  }

  return sm;
};
